import { Analytics } from '@util/Analytics';
import { NervaPartnerInsurer } from 'hydra-data-types';
import React, { ReactElement } from 'react';
import { LayoutScreenConfig, ScreenType } from '../../../config/FlowTypes';
import { store, useAppStore } from '../../../store/Store';
import { dispatchUserData } from '../../../store/userData/UserDataActions';
import { getProviderAnswer } from '../../../util/InsuranceCoverageCheck';
import {
  getPartnerInsurer,
  getPartnerInsurerAccessActions,
  getPartnerInsurerAccessUserData,
  PartnerInsurerAccessSubmission,
} from '../../../util/PartnerInsurers';
import { NavigationProps } from '../FlowNavigation';
import {
  PartnerInsurerAccessLayout,
  PartnerInsurerAccessLayoutConfig,
} from '../layouts/PartnerInsurerAccessLayout';

export type PartnerInsurerAccessScreenConfig<ScreenNames extends string> = LayoutScreenConfig<
  ScreenType.PartnerInsurerAccess,
  PartnerInsurerAccessLayoutConfig,
  ScreenNames
>;

type Props<ScreenNames extends string> = NavigationProps<
  PartnerInsurerAccessScreenConfig<ScreenNames>,
  ScreenNames
>;

/** The layout copy with `{{insurer}}` filled in with the partner's short name. */
export function withInsurerName(
  layout: PartnerInsurerAccessLayoutConfig,
  partner: NervaPartnerInsurer
): PartnerInsurerAccessLayoutConfig {
  const fill = (text: string): string => text.replace(/\{\{insurer\}\}/g, partner.displayName);
  return {
    title: fill(layout.title),
    body: layout.body.map(fill),
    inputLabel: fill(layout.inputLabel),
    inputPlaceholder: fill(layout.inputPlaceholder),
    consentLabel: fill(layout.consentLabel),
    primaryButtonTitle: fill(layout.primaryButtonTitle),
    secondaryButtonTitle: fill(layout.secondaryButtonTitle),
  };
}

/**
 * The no-cost access offer for members of a partner insurer, shown after they pick it on the
 * insurance-provider screen. Using it stores the member ID (to be shared with the insurer) and
 * applies the insurer's 100%-off code; continuing without it carries on to the usual payment path.
 * Either way the history is reset, so the choice can't be revisited with the back button.
 */
export const PartnerInsurerAccessScreen = <ScreenNames extends string>(
  props: Props<ScreenNames>
): ReactElement => {
  const partner = useAppStore((state) => getPartnerInsurer(getProviderAnswer(state.userData)));
  if (partner === undefined) {
    throw new Error(`${props.screenName} was reached without a partner insurer answer`);
  }

  const submit = (submission: PartnerInsurerAccessSubmission): void => {
    dispatchUserData(getPartnerInsurerAccessUserData(partner, submission));
    // The member ID stays out of analytics; only the choice is tracked.
    Analytics.track({
      event: Analytics.Event.o_question_answered,
      question: props.screenName,
      answer: submission.action === 'use' ? 'Yes' : 'No',
    });
    if (submission.action === 'use') {
      getPartnerInsurerAccessActions(partner).forEach((action) => store.dispatch(action));
    }
    props.navigation.next(props, { resetHistory: true });
  };

  return (
    <PartnerInsurerAccessLayout
      {...withInsurerName(props.layout, partner)}
      onUse={(memberId) => submit({ action: 'use', memberId })}
      onContinueWithout={() => submit({ action: 'continueWithout' })}
    />
  );
};
